import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import type { ChartDataPoint, ChartSeries } from './types';
import { CHART_COLOR_PALETTE } from './types';

interface LineChartProps {
  data: ChartDataPoint[];
  series?: ChartSeries[];
  dataKey?: string;
  xAxisKey?: string;
  height?: number;
  showGrid?: boolean;
  showLegend?: boolean;
  showDots?: boolean;
  strokeWidth?: number;
  yAxisRight?: {
    dataKey: string;
    name: string;
    color: string;
  };
}

export default function LineChart({
  data,
  series,
  dataKey = 'value',
  xAxisKey = 'name',
  height = 280,
  showGrid = true,
  showLegend = false,
  showDots = true,
  strokeWidth = 2,
  yAxisRight,
}: LineChartProps) {
  // If no series provided, create a default one
  const chartSeries = series || [
    { dataKey, name: dataKey, color: CHART_COLOR_PALETTE[0] },
  ];

  return (
    <ResponsiveContainer width="100%" height={height}>
      <RechartsLineChart data={data} margin={{ top: 10, right: yAxisRight ? 40 : 10, left: 0, bottom: 0 }}>
        {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />}
        <XAxis
          dataKey={xAxisKey}
          tick={{ fontSize: 11 }}
          tickLine={false}
          axisLine={{ stroke: '#e5e7eb' }}
        />
        <YAxis
          yAxisId="left"
          tick={{ fontSize: 11 }}
          tickLine={false}
          axisLine={false}
          width={40}
        />
        {yAxisRight && (
          <YAxis
            yAxisId="right"
            orientation="right"
            tick={{ fontSize: 11 }}
            tickLine={false}
            axisLine={false}
            width={40}
          />
        )}
        <Tooltip
          contentStyle={{
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '6px',
            fontSize: '12px',
          }}
        />
        {showLegend && <Legend />}
        {chartSeries.map((s, index) => (
          <Line
            key={s.dataKey}
            yAxisId="left"
            type={s.type || 'monotone'}
            dataKey={s.dataKey}
            name={s.name}
            stroke={s.color || CHART_COLOR_PALETTE[index % CHART_COLOR_PALETTE.length]}
            strokeWidth={strokeWidth}
            dot={showDots ? { r: 3 } : false}
            activeDot={{ r: 5 }}
          />
        ))}
        {yAxisRight && (
          <Line
            yAxisId="right"
            type="monotone"
            dataKey={yAxisRight.dataKey}
            name={yAxisRight.name}
            stroke={yAxisRight.color}
            strokeWidth={strokeWidth}
            dot={showDots ? { r: 3 } : false}
            activeDot={{ r: 5 }}
          />
        )}
      </RechartsLineChart>
    </ResponsiveContainer>
  );
}
